import {clsx} from 'clsx'
import type {CSSProperties} from 'react'
import styles from './WorkingAsciiLoader.module.css'

interface WorkingAsciiLoaderProps {
  ariaLabel?: string
  animated?: boolean
  className?: string
}

// Layout grid directly in CSS pixels (no scaling).
const COL_CENTERS = [2, 6] as const
const ROW_COUNT = 4
const ROW_PITCH = 3
const FIRST_ROW_Y = 2
const CANVAS_W = 8
const CANVAS_H = 13
const DOT_R = 1
const OVERSHOOT = 2
const STATIC_VISIBLE_FROM = Math.ceil(ROW_COUNT / 2)

// Timeline: fill 8 → hold 6 → empty 8 → pause 2 = 24 steps
const DOT_COUNT = ROW_COUNT * COL_CENTERS.length
const FILL_STEPS = DOT_COUNT
const HOLD_STEPS = 6
const PAUSE_STEPS = 2
const TOTAL_STEPS = FILL_STEPS + HOLD_STEPS + FILL_STEPS + PAUSE_STEPS
const STEP_MS = 140
const CYCLE_MS = TOTAL_STEPS * STEP_MS
const DOT_DIAMETER = DOT_R * 2

// Dot descriptors ordered bottom→top, left→right (fill order: sand settles
// at the bottom first, then stacks upward).
const DOTS = Array.from({length: ROW_COUNT}, (_, row) => {
  const cy = FIRST_ROW_Y + row * ROW_PITCH
  return COL_CENTERS.map((cx, col) => ({
    col,
    row,
    left: cx - DOT_R,
    top: cy - DOT_R,
    entryY: -(cy + OVERSHOOT),
    exitY: CANVAS_H - cy + OVERSHOOT,
    staticVisible: row >= STATIC_VISIBLE_FROM,
  }))
})
  .reverse()
  .flat()

function WorkingAsciiLoader({ariaLabel, animated = true, className}: WorkingAsciiLoaderProps) {
  return (
    <span
      aria-hidden={ariaLabel ? undefined : true}
      aria-label={ariaLabel}
      role={ariaLabel ? 'img' : undefined}
      className={clsx(styles.root, className)}
    >
      <span
        aria-hidden="true"
        className={styles.canvas}
        style={{
          '--canvas-w': `${CANVAS_W}px`,
          '--canvas-h': `${CANVAS_H}px`,
          ...(animated ? {'--cycle-ms': `${CYCLE_MS}ms`} : undefined),
        }}
      >
        {DOTS.map((dot, i) => {
          const style: CSSProperties = {
            left: dot.left,
            top: dot.top,
            width: DOT_DIAMETER,
            height: DOT_DIAMETER,
            ...(animated
              ? {
                  '--sand-entry-y': `${dot.entryY}px`,
                  '--sand-exit-y': `${dot.exitY}px`,
                  '--sand-delay': `${i * STEP_MS - CYCLE_MS}ms`,
                }
              : undefined),
          }

          return (
            <span
              key={`${animated ? 'a' : 's'}-${dot.col}-${dot.row}`}
              data-loader-column={dot.col}
              data-loader-row={dot.row}
              data-static-visible={dot.staticVisible}
              className={animated ? styles.dot : styles.staticDot}
              style={style}
            />
          )
        })}
      </span>
    </span>
  )
}

export {WorkingAsciiLoader}
export type {WorkingAsciiLoaderProps}
